import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { loadECharts } from "@/lib/viz/cdnLoader";
import { prepareEChartsOption } from "@/lib/viz/dataSubstitution";
import type { VizRenderPayload } from "@/lib/viz/types";

interface Props { payload: VizRenderPayload; }

export const EChartsRenderer: React.FC<Props> = ({ payload }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let instance: {
      setOption(option: Record<string, unknown>, notMerge?: boolean): void;
      resize(): void;
      dispose(): void;
    } | null = null;
    let observer: ResizeObserver | null = null;

    (async () => {
      try {
        const echarts = await loadECharts();
        if (cancelled || !containerRef.current) return;
        // SVG renderer mode for export reliability. The canvas renderer
        // is faster on large datasets but its toDataURL() output is
        // racey — capturing mid-animation produces a blank/partial PNG,
        // and "Save as Cognos report" then ships a table-only report
        // because captureVizImage rejects the degenerate data URL. SVG
        // mode renders synchronously into the DOM, captureVizImage's
        // SVG → PNG rasterizer handles it deterministically, and the
        // saved Cognos report embeds the actual chart the user saw.
        // Operate's chart sizes (< 10K points typically) don't hit the
        // SVG-renderer perf cliff.
        instance = echarts.init(containerRef.current, null, { renderer: "svg" });
        const option = prepareEChartsOption(payload);
        // Animations off — same reason as the SVG renderer choice
        // above: a paused chart is the most reliable export source,
        // and operate-ui charts don't benefit from animation polish.
        instance.setOption({ ...option, animation: false }, true);
        observer = new ResizeObserver(() => instance?.resize());
        observer.observe(containerRef.current);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "unknown error";
          setError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { observer?.disconnect(); } catch { /* no-op */ }
      try { instance?.dispose(); } catch { /* no-op */ }
    };
  }, [payload]);

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
        <div className="font-semibold">ECharts chart could not render</div>
        <div className="mt-1 text-xs text-rose-700">{error}</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      // h-full fills the fullscreen-viewer parent (which gives real
      // height via flex-1); min-h-[360px] preserves the inline
      // chat-card size when the parent has no height. max-h-screen
      // is a safety cap so the chart never overshoots the viewport
      // in pathological flex contexts.
      className="w-full h-full min-h-[360px] max-h-screen overflow-hidden"
    />
  );
};
